import {
  StrapiMediaInfo,
  StrapiMediaImage,
} from 'apolloClient/types/StrapiMediaInfo';

import { defaultImage } from 'src/constants/constants';

export enum ImageSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  THUMBNAIL = 'thumbnail',
  LARGE = 'large',
  ORIGINAL = 'original',
}

const imagePrefix = {
  [ImageSize.SMALL]: 'small',
  [ImageSize.MEDIUM]: 'medium',
  [ImageSize.THUMBNAIL]: 'thumbnail',
  [ImageSize.LARGE]: 'large',
  [ImageSize.ORIGINAL]: '',
};

// Define fallback order for each size - only equal or larger sizes (except thumbnail)
const fallbackOrder: Record<ImageSize, ImageSize[]> = {
  [ImageSize.THUMBNAIL]: [], // Thumbnail only returns thumbnail - no fallbacks for blur usage
  [ImageSize.SMALL]: [ImageSize.MEDIUM, ImageSize.LARGE], // Small -> Medium -> Large
  [ImageSize.MEDIUM]: [ImageSize.LARGE], // Medium -> Large only
  [ImageSize.LARGE]: [], // Large has no larger format to fall back to
  [ImageSize.ORIGINAL]: [ImageSize.LARGE, ImageSize.MEDIUM, ImageSize.SMALL], // Original can fall back to any
};

/**
 * Checks if a specific image format exists in the image data
 */
function hasImageFormat(image: StrapiMediaInfo, size: ImageSize): boolean {
  if (!image?.formats) return false;
  if (size === ImageSize.ORIGINAL) return false; // Original doesn't exist in formats
  return Boolean(image.formats[size]);
}

/**
 * Gets the image format data for a specific size
 */
function getImageFormatData(
  image: StrapiMediaInfo,
  size: ImageSize
): StrapiMediaImage | null {
  if (!image?.formats) return null;
  if (size === ImageSize.ORIGINAL) return null; // Original doesn't exist in formats
  return image.formats[size] || null;
}

/**
 * Finds the best available format with fallback logic (only equal or larger sizes)
 */
function findBestAvailableFormat(
  image: StrapiMediaInfo,
  preferredSize: ImageSize
): ImageSize | null {
  // First check if preferred size exists
  if (hasImageFormat(image, preferredSize)) {
    return preferredSize;
  }

  // For thumbnail, no fallbacks - return null if thumbnail doesn't exist
  if (preferredSize === ImageSize.THUMBNAIL) {
    return null;
  }

  // Check fallback options in order (only larger sizes)
  const fallbacks = fallbackOrder[preferredSize];
  for (const fallbackSize of fallbacks) {
    if (hasImageFormat(image, fallbackSize)) {
      return fallbackSize;
    }
  }

  return null;
}

/**
 * Removes any existing size prefix from the hash
 */
function normalizeHash(hash: string): string {
  const prefixes = Object.values(imagePrefix).filter((prefix) => prefix !== '');

  for (const prefix of prefixes) {
    if (hash.startsWith(`${prefix}_`)) {
      return hash.substring(prefix.length + 1);
    }
  }

  return hash;
}

/**
 * Constructs the CDN URL for a given format
 */
function buildCdnUrl(formatData: StrapiMediaImage, size: ImageSize): string {
  const { hash, ext } = formatData;
  const baseUrl = process.env.NEXT_PUBLIC_CDN_URL || '';
  const prefix = imagePrefix[size];

  // Always normalize hash first, then add needed prefix
  const cleanHash = normalizeHash(hash);

  return `${baseUrl}/${prefix ? `${prefix}_` : ''}${cleanHash}${ext}`;
}

/**
 * Gets the original image URL (fallback when no formats are available)
 */
function getOriginalImageUrl(image: StrapiMediaInfo): string {
  if (!image?.url) return '';

  // If we have CDN base URL, we should ideally construct the URL properly
  // But since original doesn't have format data, we return the original URL
  // You might want to modify this based on your CDN structure
  return image.url;
}

/**
 * Main function to generate image URL with comprehensive fallback logic
 * - Only falls back to equal or larger sizes (except original)
 * - Thumbnail only returns thumbnail (no fallbacks for blur)
 */
export function generateImageUrlFromImage(
  image: StrapiMediaInfo,
  imageSize: ImageSize
): string {
  // Early return if image is invalid
  if (!image) {
    return '';
  }

  // For original size, we have special handling since it doesn't exist in formats
  if (imageSize === ImageSize.ORIGINAL) {
    // Try to find the best available format, preferring larger sizes
    const bestAvailableSize = findBestAvailableFormat(
      image,
      ImageSize.ORIGINAL
    );

    if (bestAvailableSize) {
      const formatData = getImageFormatData(image, bestAvailableSize);
      if (formatData) {
        return buildCdnUrl(formatData, imageSize);
      }
    }

    // Fallback to original URL
    return getOriginalImageUrl(image);
  }

  // For thumbnail, only return thumbnail or empty string (no fallbacks for blur)
  if (imageSize === ImageSize.THUMBNAIL) {
    if (hasImageFormat(image, ImageSize.THUMBNAIL)) {
      const formatData = getImageFormatData(image, ImageSize.THUMBNAIL);
      if (formatData) {
        return buildCdnUrl(formatData, ImageSize.THUMBNAIL);
      }
    }
    return ''; // No fallback for thumbnail
  }

  // For non-original sizes, use the fallback logic (only larger sizes)
  const bestAvailableSize = findBestAvailableFormat(image, imageSize);

  if (bestAvailableSize) {
    const formatData = getImageFormatData(image, bestAvailableSize);
    if (formatData) {
      return buildCdnUrl(formatData, bestAvailableSize);
    }
  }

  // Final fallback to original URL (only for non-thumbnail sizes)
  return getOriginalImageUrl(image);
}

// Convenience functions for specific sizes
export function getSmallImageUrlFromImage(image: StrapiMediaInfo): string {
  return generateImageUrlFromImage(image, ImageSize.SMALL);
}

export function getMediumImageUrlFromImage(image: StrapiMediaInfo): string {
  return generateImageUrlFromImage(image, ImageSize.MEDIUM);
}

export function getThumbnailImageUrlFromImage(image: StrapiMediaInfo): string {
  return generateImageUrlFromImage(image, ImageSize.THUMBNAIL) || defaultImage;
}

export function getLargeImageUrlFromImage(image: StrapiMediaInfo): string {
  return generateImageUrlFromImage(image, ImageSize.LARGE);
}

export function getOriginalImageUrlFromImage(image: StrapiMediaInfo): string {
  return generateImageUrlFromImage(image, ImageSize.ORIGINAL);
}

// Optional: Helper function to get information about what format was actually used
export function getActualImageFormat(
  image: StrapiMediaInfo,
  requestedSize: ImageSize
): {
  actualSize: ImageSize | null;
  isOriginal: boolean;
  url: string;
} {
  if (!image) {
    return { actualSize: null, isOriginal: false, url: '' };
  }

  const bestAvailableSize = findBestAvailableFormat(image, requestedSize);
  const url = generateImageUrlFromImage(image, requestedSize);

  return {
    actualSize: bestAvailableSize,
    isOriginal: !bestAvailableSize,
    url,
  };
}

export interface ProcessedImage {
  url: string;
  width: number;
  height: number;
  alt: string;
  originalImage: StrapiMediaInfo;
  blurDataURL: string;
}

export function processImages(
  images: StrapiMediaInfo[],
  altPrefix: string
): ProcessedImage[] {
  return images
    .map((image, index) => {
      // Find the best available format for medium
      const bestFormat = findBestAvailableFormat(image, ImageSize.MEDIUM);
      let url = '';
      let width: number | undefined;
      let height: number | undefined;

      if (bestFormat) {
        const formatData = getImageFormatData(image, bestFormat);
        if (formatData) {
          url = generateImageUrlFromImage(image, bestFormat);
          width = formatData.width;
          height = formatData.height;
        }
      }

      // Fallback to original if no format found
      if (!url && image.url) {
        url = image.url;
        width = image.width;
        height = image.height;
      }

      // Fallback to thumbnail if no width
      if (!width && image.formats && image.formats.thumbnail) {
        const formatData = getImageFormatData(image, ImageSize.THUMBNAIL);
        if (formatData) {
          width = formatData.width;
          height = formatData.height;
        }
      }

      const blurDataURL = getThumbnailImageUrlFromImage(image);

      if (!url) {
        return null;
      }

      return {
        url,
        width,
        height,
        alt: `${altPrefix} image #${index + 1}`,
        originalImage: image,
        blurDataURL,
      };
    })
    .filter((img): img is ProcessedImage => img !== null);
}
